/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        CAT Document Export & Reassembly Utility (Task 10.5.3)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { CatSegment } from '../stores/useCatEditorStore';

export interface ExportProjectMeta {
  title: string;
  sourceLang: string;
  targetLang: string;
  fileName?: string;
  domain?: string;
}

export interface ExportOptions {
  includeUntranslated?: boolean;
  includeQa?: boolean;
  includeMetadata?: boolean;
}

/**
 * Escapes special XML characters to prevent malformed tags.
 */
export function escapeXml(str: string): string {
  if (!str) return '';
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

/**
 * Generates valid XLIFF 1.2 XML string.
 */
export function generateXliff12(
  project: ExportProjectMeta,
  segments: CatSegment[],
  options: ExportOptions = { includeUntranslated: true }
): string {
  const units = segments
    .filter((s) => options.includeUntranslated || s.targetText.trim().length > 0)
    .map((seg) => {
      let state = 'new';
      if (seg.status === 'approved') state = 'signed-off';
      else if (seg.status === 'draft') state = 'needs-review-translation';

      const targetVal = seg.targetText || (options.includeUntranslated ? seg.sourceText : '');
      return `      <trans-unit id="${escapeXml(seg.id)}" approved="${seg.status === 'approved' ? 'yes' : 'no'}">
        <source>${escapeXml(seg.sourceText)}</source>
        <target state="${state}">${escapeXml(targetVal)}</target>
      </trans-unit>`;
    })
    .join('\n');

  return `<?xml version="1.0" encoding="UTF-8"?>
<xliff version="1.2" xmlns="urn:oasis:names:tc:xliff:document:1.2">
  <file source-language="${escapeXml(project.sourceLang)}" target-language="${escapeXml(project.targetLang)}" datatype="plaintext" original="${escapeXml(project.fileName || project.title)}">
    <header>
      <tool tool-id="sophia-cat" tool-name="Sophia Platform CAT Studio" tool-version="2.0"/>
    </header>
    <body>
${units}
    </body>
  </file>
</xliff>`;
}

/**
 * Generates valid TMX 1.4b (Translation Memory eXchange) XML string.
 */
export function generateTmx(
  project: ExportProjectMeta,
  segments: CatSegment[]
): string {
  const translatedSegments = segments.filter((s) => s.targetText.trim().length > 0);

  const units = translatedSegments
    .map((seg) => {
      const nowIso = new Date().toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
      return `    <tu tuid="${escapeXml(seg.id)}" creationdate="${nowIso}">
      <tuv xml:lang="${escapeXml(project.sourceLang)}">
        <seg>${escapeXml(seg.sourceText)}</seg>
      </tuv>
      <tuv xml:lang="${escapeXml(project.targetLang)}">
        <seg>${escapeXml(seg.targetText)}</seg>
      </tuv>
    </tu>`;
    })
    .join('\n');

  return `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE tmx SYSTEM "tmx14.dtd">
<tmx version="1.4">
  <header creationtool="SophiaPlatform" creationtoolversion="2.0" segtype="sentence" o-tmf="UTF-8" adminlang="en" srclang="${escapeXml(project.sourceLang)}" datatype="PlainText">
  </header>
  <body>
${units}
  </body>
</tmx>`;
}

/**
 * Generates a clean, styled HTML bilingual review/acceptance table.
 */
export function generateBilingualHtml(
  project: ExportProjectMeta,
  segments: CatSegment[],
  options: ExportOptions = { includeQa: true, includeUntranslated: true }
): string {
  const rows = segments
    .map((seg, idx) => {
      const targetDisplay = seg.targetText.trim()
        ? escapeXml(seg.targetText)
        : `<em style="color:#94a3b8;">${escapeXml(seg.sourceText)} (untranslated)</em>`;

      const statusBadge =
        seg.status === 'approved'
          ? '<span style="background:#10b981;color:#fff;padding:2px 8px;border-radius:9999px;font-size:11px;font-weight:600;">Approved</span>'
          : seg.status === 'draft'
          ? '<span style="background:#f59e0b;color:#fff;padding:2px 8px;border-radius:9999px;font-size:11px;font-weight:600;">Draft</span>'
          : '<span style="background:#64748b;color:#fff;padding:2px 8px;border-radius:9999px;font-size:11px;font-weight:600;">New</span>';

      const qaIssues =
        options.includeQa && seg.qaWarnings && seg.qaWarnings.length > 0
          ? `<div style="margin-top:6px;font-size:11px;color:#ef4444;">⚠️ ${seg.qaWarnings.map(escapeXml).join('; ')}</div>`
          : '';

      return `      <tr style="border-bottom:1px solid #e2e8f0;">
        <td style="padding:12px;color:#64748b;font-size:12px;vertical-align:top;text-align:center;">#${idx + 1}</td>
        <td style="padding:12px;vertical-align:top;line-height:1.6;">${escapeXml(seg.sourceText)}</td>
        <td style="padding:12px;vertical-align:top;line-height:1.6;">${targetDisplay}${qaIssues}</td>
        <td style="padding:12px;vertical-align:top;text-align:center;">${statusBadge}</td>
      </tr>`;
    })
    .join('\n');

  return `<!DOCTYPE html>
<html lang="${escapeXml(project.targetLang)}">
<head>
  <meta charset="UTF-8">
  <title>${escapeXml(project.title)} — Bilingual Verification Report</title>
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; margin: 40px; color: #1e293b; background: #f8fafc; }
    .container { max-width: 1200px; margin: 0 auto; background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05); overflow: hidden; }
    .header { padding: 24px 32px; background: #0f172a; color: #fff; }
    .header h1 { margin: 0 0 8px 0; font-size: 22px; font-weight: 700; }
    .meta-tags { display: flex; gap: 16px; font-size: 13px; color: #94a3b8; }
    .meta-tag { background: rgba(255,255,255,0.1); padding: 4px 10px; border-radius: 6px; }
    table { width: 100%; border-collapse: collapse; text-align: left; }
    th { background: #f1f5f9; padding: 14px 12px; font-size: 12px; font-weight: 700; color: #475569; text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 2px solid #cbd5e1; }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>${escapeXml(project.title)}</h1>
      <div class="meta-tags">
        <span class="meta-tag">Pair: ${escapeXml(project.sourceLang.toUpperCase())} &rarr; ${escapeXml(project.targetLang.toUpperCase())}</span>
        <span class="meta-tag">Domain: ${escapeXml(project.domain || 'General')}</span>
        <span class="meta-tag">Segments: ${segments.length}</span>
        <span class="meta-tag">Exported: ${new Date().toLocaleDateString()}</span>
      </div>
    </div>
    <table>
      <thead>
        <tr>
          <th style="width: 5%;">No</th>
          <th style="width: 45%;">Source Segment (${escapeXml(project.sourceLang.toUpperCase())})</th>
          <th style="width: 40%;">Target Translation (${escapeXml(project.targetLang.toUpperCase())})</th>
          <th style="width: 10%; text-align: center;">Status</th>
        </tr>
      </thead>
      <tbody>
${rows}
      </tbody>
    </table>
  </div>
</body>
</html>`;
}

/**
 * Generates UTF-8 CSV with BOM for proper Excel character encoding.
 */
export function generateCsvExport(
  segments: CatSegment[],
  options: ExportOptions = { includeUntranslated: true }
): string {
  // UTF-8 BOM so Excel opens Vietnamese and international characters correctly
  const BOM = '\uFEFF';
  const headers = ['Segment ID', 'Order', 'Source Text', 'Target Text', 'Status', 'Word Count', 'TM Match'];

  const rows = segments
    .filter((s) => options.includeUntranslated || s.targetText.trim().length > 0)
    .map((seg, idx) => {
      const source = `"${(seg.sourceText || '').replace(/"/g, '""')}"`;
      const target = `"${(seg.targetText || '').replace(/"/g, '""')}"`;
      const match = `${seg.tmMatchPercent || 0}%`;
      return [seg.id, idx + 1, source, target, seg.status, seg.wordCount, match].join(',');
    });

  return BOM + [headers.join(','), ...rows].join('\r\n');
}

/**
 * Reassembles clean target text formatted with double newlines between segments.
 */
export function generateTargetDocumentText(
  segments: CatSegment[],
  fallbackSource = true
): string {
  return segments
    .map((s) => {
      if (s.targetText && s.targetText.trim().length > 0) {
        return s.targetText.trim();
      }
      return fallbackSource ? s.sourceText.trim() : '';
    })
    .filter((t) => t.length > 0)
    .join('\n\n');
}

/**
 * Triggers a browser file download from Blob without server egress fees ($0 egress).
 */
export function triggerFileDownload(content: string, fileName: string, mimeType: string): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
